import React, { useMemo, useState } from "react";
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import ColumnFilter from "./ColoumnFilter";
import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";
import { DebouncedInput } from "./Assets";
import {
    AscendingIcon,
    ClearSortIcon,
    DescendingIcon,
    MicrochevronDoubleRightIcon,
} from "assets";
declare module "@tanstack/react-table" {
    interface FilterFns {
        fuzzy: FilterFn<unknown>;
    }
    interface FilterMeta {
        itemRank: RankingInfo;
    }
}
interface Props<TData> {
    data: TData | TData[];
    tableColumns: ColumnDef<TData, any>[];
    columnVisibilityItems?: VisibilityState;
    addButton: React.ReactNode;
    headers: string[];
    onPaginationChange?: (limit: number, offset: number) => void;
    pageIndex: number;        // ← add here
    pageSize: number;         // ← add here
    hasNext: boolean;

}
function ServerTable<TData>({
    data,
    tableColumns,
    columnVisibilityItems,
    addButton,
    headers,
    onPaginationChange,
    pageIndex,
    pageSize,
    hasNext,

}: Props<TData>) {
    const loading = false;
    const error = null;
    const fuzzyFilter: FilterFn<{ [key: string]: string }> = (
        row,
        columnId,
        value,
        addMeta
    ) => {
        const itemRank = rankItem(row.getValue(columnId), value);
        addMeta({
            itemRank,
        });
        return itemRank.passed;
    };

    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        columnVisibilityItems ?? {}
    );

    const memoizedColumns = useMemo(() => tableColumns, [tableColumns]);
    const memoizedData = useMemo(() => {
        if (!data) return [];
        return Array.isArray(data) ? data : [data];
    }, [data]);
    const filteredRowModel = useMemo(
        () => getFilteredRowModel(),
        [columnFilters, globalFilter]
    );
    const sortedRowModel = useMemo(() => getSortedRowModel(), [sorting]);
    const paginationRowModel = useMemo(
        () => getPaginationRowModel(),
        [sorting, columnFilters]
    );

    const table = useReactTable({
        data: memoizedData,
        columns: memoizedColumns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            columnVisibility,
            sorting,
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: "fuzzy",
        getFilteredRowModel: filteredRowModel,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: sortedRowModel,
        onSortingChange: setSorting,
        getPaginationRowModel: paginationRowModel,
        debugTable: false,
        debugHeaders: false,
        debugColumns: false,
        manualPagination: true,
        pageCount: hasNext ? pageIndex + 2 : pageIndex + 1,
    });
    const startRow =
        table.getState().pagination.pageIndex *
        table.getState().pagination.pageSize +
        1;
    const endRow = startRow + table.getRowModel().rows.length - 1;
    const totalRows = table.getRowCount();
    const nextPage = () => {
        if (!hasNext) return;
        onPaginationChange?.(pageSize, (pageIndex + 1) * pageSize);
    };

    const prevPage = () => {
        if (pageIndex === 0) return;
        onPaginationChange?.(pageSize, (pageIndex - 1) * pageSize);
    };
    return (
        <div className="px-8 py-12">
            <div className="flex justify-between">
                <span>
                    <h1 className="text-lg text-primary-900 flex items-center gap-1">
                        {headers?.map((header, index) => (
                            <React.Fragment key={index}>
                                <span
                                    className={
                                        index === headers.length - 1 ? "text-primary-400" : ""
                                    }
                                >
                                    {header}
                                </span>
                                {index < headers.length - 1 && (
                                    <MicrochevronDoubleRightIcon className="size-3.5" />
                                )}
                            </React.Fragment>
                        ))}
                    </h1>
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                        <div>
                            Showing <b>{startRow}</b> - <b>{endRow}</b> of <b>{totalRows}</b>{" "}
                            results
                        </div>
                    </span>
                </span>

                <div className="flex place-items-center gap-2">
                    <ColumnFilter columns={table.getAllLeafColumns()} />
                    {addButton}
                </div>
            </div>
            <div className="h-4" />
            <div className="w-full ">
                <DebouncedInput
                    value={globalFilter ?? ""}
                    onChange={(value) => setGlobalFilter(String(value))}
                    className="p-2 font-sm shadow border border-block rounded-lg"
                    placeholder="Contains text..."
                />
            </div>
            <div className="h-2" />
            <div className=" overflow-x-auto">
                <table className="w-full text-sm text-left rtl:text-right text-primary-900 dark:text-gray-400 overflow-x-scroll">
                    {/* <thead className="text-xs text-white bg-primary-500 dark:bg-gray-700 dark:text-gray-400">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr
                                key={headerGroup.id}
                                className="border-b divide-x divide-gray-200"
                            >
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className="py-2 text-center"
                                    >
                                        {header.isPlaceholder ? null : (
                                            <>
                                                <div
                                                    className={
                                                        header.column.getCanSort()
                                                            ? "cursor-pointer select-none flex justify-center"
                                                            : ""
                                                    }
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    title={
                                                        header.column.getCanSort()
                                                            ? header.column.getNextSortingOrder() === "asc"
                                                                ? "Sort ascending"
                                                                : header.column.getNextSortingOrder() === "desc"
                                                                    ? "Sort descending"
                                                                    : "Clear sort"
                                                            : undefined
                                                    }
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {{
                                                        asc: <AscendingIcon />,
                                                        desc: <DescendingIcon />,
                                                    }[header.column.getIsSorted() as string] ??
                                                        (header.column.getCanSort() ? (
                                                            <ClearSortIcon />
                                                        ) : null)}
                                                </div>
                                            </>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead> */}
                    <thead className="text-xs text-white bg-primary-500 dark:bg-gray-700 dark:text-gray-400">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} className="border-b divide-x divide-gray-200">
                                {/* Sl. No header */}
                                <th className="py-2 px-2 text-center">Sl. No</th>

                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className="py-2 text-center"
                                    >
                                        {header.isPlaceholder ? null : (
                                            <>
                                                <div
                                                    className={
                                                        header.column.getCanSort()
                                                            ? "cursor-pointer select-none flex justify-center"
                                                            : ""
                                                    }
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    title={
                                                        header.column.getCanSort()
                                                            ? header.column.getNextSortingOrder() === "asc"
                                                                ? "Sort ascending"
                                                                : header.column.getNextSortingOrder() === "desc"
                                                                    ? "Sort descending"
                                                                    : "Clear sort"
                                                            : undefined
                                                    }
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {{
                                                        asc: <AscendingIcon />,
                                                        desc: <DescendingIcon />,
                                                    }[header.column.getIsSorted() as string] ?? (header.column.getCanSort() ? (
                                                        <ClearSortIcon />
                                                    ) : null)}
                                                </div>
                                            </>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {data &&
                            table.getRowModel().rows.map((row, rowIndex) => {
                                // Compute serial number based on current page
                                const slNo = pageIndex * pageSize + rowIndex + 1;

                                return (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-primary-400 hover:text-white odd:bg-white odd:dark:bg-gray-900 even:bg-tableColor-100 even:dark:bg-gray-800 border-b dark:border-gray-700"
                                    >
                                        {/* First column for Sl. No */}
                                        <td className="py-2 px-2 text-center">{slNo}</td>

                                        {/* Render other columns dynamically */}
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="py-2 px-2">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        {loading && (
                            <tr>
                                <td colSpan={table.getAllColumns().length}>Loading ...</td>
                            </tr>
                        )}
                        {error && (
                            <tr>
                                <td colSpan={table.getAllColumns().length}>{error}</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        {table.getFooterGroups().map((footerGroup) => (
                            <tr key={footerGroup.id}>
                                {footerGroup.headers.map((header) => (
                                    <th key={header.id} colSpan={header.colSpan}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.footer,
                                                header.getContext()
                                            )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </tfoot>
                </table>
            </div>
            <div className="h-2" />
            <div className="flex justify-between text-sm text-gray-500">
                <div className="flex space-x-1">
                    <button
                        className="border px-2"
                        // onClick={() => table.setPageIndex(0)}
                        onClick={() => {
                            table.previousPage();
                            const pageIndex = table.getState().pagination.pageIndex - 1;
                            const limit = table.getState().pagination.pageSize;
                            const offset = pageIndex * limit;
                            onPaginationChange?.(limit, offset);
                        }}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"1"}
                    </button>
                    <button
                        className="border px-2"
                        // onClick={() => table.previousPage()}
                        onClick={() => {
                            table.previousPage();
                            const pageIndex = table.getState().pagination.pageIndex - 1;
                            const limit = table.getState().pagination.pageSize;
                            const offset = pageIndex * limit;
                            onPaginationChange?.(limit, offset);
                        }}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<"}
                    </button>
                    <button
                        className="border px-2"
                        // onClick={() => table.nextPage()}
                        onClick={() => {
                            table.nextPage();
                            const pageIndex = table.getState().pagination.pageIndex + 1;
                            const limit = table.getState().pagination.pageSize;
                            const offset = pageIndex * limit;
                            onPaginationChange?.(limit, offset);
                        }}
                        disabled={!table.getCanNextPage()}
                    >
                        {">"}
                    </button>
                    <button
                        className="border px-2"
                        // onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        onClick={() => {
                            table.nextPage();
                            const pageIndex = table.getState().pagination.pageIndex + 1;
                            const limit = table.getState().pagination.pageSize;
                            const offset = pageIndex * limit;
                            onPaginationChange?.(limit, offset);
                        }}
                        disabled={!table.getCanNextPage()}
                    >
                        {Number(table.getPageCount()) === 0 ? "1" : table.getPageCount()}
                    </button>
                </div>
                <div>
                    <span className="flex items-center gap-1">
                        Go to page:
                        <input
                            type="number"
                            min="1"
                            max={table.getPageCount()}
                            defaultValue={table.getState().pagination.pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            className="border px-2 py-0.5 w-16 outline-none"
                        />
                    </span>
                </div>
                <div>
                    <label>Result per page </label>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        className="border border-gray-300 px-2 py-1 outline-none"
                    >
                        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}
export default ServerTable;
